import { Controller } from "@hotwired/stimulus"
import { Map, GeoJSON, CircleMarker, Control, DomUtil, TileLayer } from "leaflet"
import { union } from "@turf/union"
import "leaflet/dist/leaflet.css"
import "./africa_map.css"

// Site marker colours (per individual site status).
const STATUS_COLORS = {
  operational: "#16a34a",
  maintenance: "#ca8a04",
  inactive:    "#6b7280",
  planned:     "#2563eb",
}

// Country deployment status colours (per Amp::Country#status). Kept in
// sync with Amp::Country::STATUS_COLORS on the Ruby side.
const COUNTRY_STATUS_COLORS = {
  active:         "#1B9DD9",  // blue   — "Active"
  in_development: "#F4A24B",  // orange — "In Development"
  planned:        "#D9D9D9",  // grey   — "Planned"
}

// Fill for African countries with no Amp::Country record: white, overlaid
// with a diagonal hatch pattern ("Non-AMP Country").
const NON_AMP_FILL = "#ffffff"
const HATCH_ID     = "amp-nonamp-hatch"

// Inline heroicon-style SVGs for the country-overview popup metric cards.
const ovIcon = (paths) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ` +
  `stroke-linecap="round" stroke-linejoin="round" width="26" height="26" aria-hidden="true">${paths}</svg>`

const OVERVIEW_ICONS = {
  // cog-6-tooth — Potential Sites
  potential: ovIcon('<path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.43.992a7.7 7.7 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.5 6.5 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.5 6.5 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.9 6.9 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"/><circle cx="12" cy="12" r="3"/>'),
  // check-circle — Active Sites
  active: ovIcon('<path d="M9 12.75 11.25 15 15 9.75"/><circle cx="12" cy="12" r="9"/>'),
  // user-group — Beneficiaries
  people: ovIcon('<path d="M18 18.72a9.09 9.09 0 0 0 3.74-.48 3 3 0 0 0-4.68-2.72M18 18.72c0 .22-.01.44-.04.66A11.94 11.94 0 0 1 12 21c-2.17 0-4.2-.58-5.96-1.58A6.06 6.06 0 0 1 6 18.72m12 0a5.97 5.97 0 0 0-.94-3.2M6 18.72a8.99 8.99 0 0 1-3.74-.48 3 3 0 0 1 4.68-2.72M6 18.72a5.97 5.97 0 0 1 .94-3.2m0 0A6 6 0 0 1 12 12.75a6 6 0 0 1 5.06 2.77M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"/>'),
}

// ISO numeric ID → { iso2, name } for African countries (Natural Earth / world-atlas)
const AFRICA = {
  12:  { iso2: "DZ", name: "Algeria" },
  24:  { iso2: "AO", name: "Angola" },
  204: { iso2: "BJ", name: "Benin" },
  72:  { iso2: "BW", name: "Botswana" },
  854: { iso2: "BF", name: "Burkina Faso" },
  108: { iso2: "BI", name: "Burundi" },
  120: { iso2: "CM", name: "Cameroon" },
  132: { iso2: "CV", name: "Cape Verde" },
  140: { iso2: "CF", name: "Central African Republic" },
  148: { iso2: "TD", name: "Chad" },
  174: { iso2: "KM", name: "Comoros" },
  178: { iso2: "CG", name: "Congo" },
  180: { iso2: "CD", name: "DR Congo" },
  262: { iso2: "DJ", name: "Djibouti" },
  818: { iso2: "EG", name: "Egypt" },
  226: { iso2: "GQ", name: "Equatorial Guinea" },
  232: { iso2: "ER", name: "Eritrea" },
  231: { iso2: "ET", name: "Ethiopia" },
  266: { iso2: "GA", name: "Gabon" },
  270: { iso2: "GM", name: "Gambia" },
  288: { iso2: "GH", name: "Ghana" },
  324: { iso2: "GN", name: "Guinea" },
  624: { iso2: "GW", name: "Guinea-Bissau" },
  384: { iso2: "CI", name: "Ivory Coast" },
  404: { iso2: "KE", name: "Kenya" },
  426: { iso2: "LS", name: "Lesotho" },
  430: { iso2: "LR", name: "Liberia" },
  434: { iso2: "LY", name: "Libya" },
  450: { iso2: "MG", name: "Madagascar" },
  454: { iso2: "MW", name: "Malawi" },
  466: { iso2: "ML", name: "Mali" },
  478: { iso2: "MR", name: "Mauritania" },
  480: { iso2: "MU", name: "Mauritius" },
  504: { iso2: "MA", name: "Morocco" },
  508: { iso2: "MZ", name: "Mozambique" },
  516: { iso2: "NA", name: "Namibia" },
  562: { iso2: "NE", name: "Niger" },
  566: { iso2: "NG", name: "Nigeria" },
  646: { iso2: "RW", name: "Rwanda" },
  678: { iso2: "ST", name: "Sao Tome and Principe" },
  686: { iso2: "SN", name: "Senegal" },
  694: { iso2: "SL", name: "Sierra Leone" },
  706: { iso2: "SO", name: "Somalia" },
  710: { iso2: "ZA", name: "South Africa" },
  728: { iso2: "SS", name: "South Sudan" },
  729: { iso2: "SD", name: "Sudan" },
  748: { iso2: "SZ", name: "Eswatini" },
  834: { iso2: "TZ", name: "Tanzania" },
  768: { iso2: "TG", name: "Togo" },
  788: { iso2: "TN", name: "Tunisia" },
  800: { iso2: "UG", name: "Uganda" },
  732: { iso2: "EH", name: "Western Sahara" },
  894: { iso2: "ZM", name: "Zambia" },
  716: { iso2: "ZW", name: "Zimbabwe" },
}

// The world-atlas dataset lists Somaliland as its own feature (no ISO
// numeric id), separate from Somalia. This app treats it as part of
// Somalia (iso2 "SO"), so `_mergeSomaliland` unions the two shapes into one
// feature before rendering — a single border/tooltip/click target, not two.
const SOMALIA_ID = 706

// Minimal TopoJSON → GeoJSON feature extractor (no external lib needed)
function topoFeature(topology, objectName) {
  const obj   = topology.objects[objectName]
  const scale = topology.transform?.scale    || [1, 1]
  const trans = topology.transform?.translate || [0, 0]

  function decodeArc(idx) {
    const reversed = idx < 0
    const arc      = topology.arcs[reversed ? ~idx : idx]
    let x = 0, y = 0
    const coords = arc.map(([dx, dy]) => {
      x += dx; y += dy
      return [x * scale[0] + trans[0], y * scale[1] + trans[1]]
    })
    return reversed ? coords.reverse() : coords
  }

  function convertGeometry(geom) {
    if (!geom) return null
    if (geom.type === "Polygon") {
      return { type: "Polygon", coordinates: geom.arcs.map(ring => ring.flatMap(decodeArc)) }
    }
    if (geom.type === "MultiPolygon") {
      return { type: "MultiPolygon", coordinates: geom.arcs.map(poly => poly.map(ring => ring.flatMap(decodeArc))) }
    }
    return null
  }

  return {
    type: "FeatureCollection",
    features: (obj.geometries || []).map(geom => ({
      type:       "Feature",
      id:         geom.id,
      properties: geom.properties || {},
      geometry:   convertGeometry(geom),
    })).filter(f => f.geometry),
  }
}

// HTML-escapes a string for safe interpolation into tooltip markup.
function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

// Thousands-separated integer/float for display.
function fmtNum(v) {
  if (v == null || v === "") return null
  const n = Number(v)
  if (!isFinite(n)) return null
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

export default class extends Controller {
  static targets = ["map", "legend", "detail"]
  static values = {
    countries:    Object,
    ampCountries: Object,
    labels:       Object,
    sites:        Array,
  }

  connect() {
    // Prefer the dedicated map target; fall back to the controller element so
    // an older mount (no map/legend/detail targets) still renders a map.
    this._mapEl = this.hasMapTarget ? this.mapTarget : this.element
    this._map = new Map(this._mapEl, {
      zoomControl:        true,
      scrollWheelZoom:    true,
      zoomSnap:           0.25,
      zoomDelta:          0.5,
      maxBoundsViscosity: 1.0,
    })

    // OpenStreetMap basemap below the country polygons (attribution shown per
    // OSM usage policy). Country fills are softened so it stays visible.
    new TileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom:     18,
    }).addTo(this._map)

    this._layersByIso = {}
    this._selectedIso = null
    this._showLegend()

    // The map container is flex: 1 inside a stretched flex row, so its height
    // tracks the sidebar. When the sidebar grows / shrinks (e.g. the user
    // clicks a country and the detail panel opens), Leaflet needs to
    // invalidate its cached size or the tile pane and centre drift.
    if (typeof ResizeObserver !== "undefined") {
      this._resizeObs = new ResizeObserver(() => {
        clearTimeout(this._resizeTimer)
        this._resizeTimer = setTimeout(() => this._map?.invalidateSize({ animate: false }), 100)
      })
      this._resizeObs.observe(this._mapEl)
    }

    fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json")
      .then(r => r.json())
      .then(world => this._render(world))
      .catch(e => console.error("Africa map: failed to load world data", e))
  }

  disconnect() {
    this._resizeObs?.disconnect()
    clearTimeout(this._resizeTimer)
    this._map?.remove()
    this._map = null
  }

  // Unions the Somaliland feature into Somalia's geometry (see SOMALIA_ID
  // comment above) so the two render as a single shape. Mutates `topo.features`
  // in place; no-ops if either feature is missing from this dataset revision.
  _mergeSomaliland(topo) {
    const somaliIdx     = topo.features.findIndex(f => +f.id === SOMALIA_ID)
    const somalilandIdx = topo.features.findIndex(f => f.properties?.name === "Somaliland")
    if (somaliIdx === -1 || somalilandIdx === -1) return

    const somalia    = topo.features[somaliIdx]
    const somaliland = topo.features[somalilandIdx]
    let merged
    try {
      merged = union({ type: "FeatureCollection", features: [somalia, somaliland] })
    } catch (e) {
      console.error("Africa map: failed to union Somalia/Somaliland", e)
      return
    }
    if (!merged) return

    topo.features[somaliIdx] = { ...somalia, geometry: merged.geometry }
    topo.features.splice(somalilandIdx, 1)
  }

  _render(world) {
    const all = topoFeature(world, "countries")
    this._mergeSomaliland(all)
    const africaGeo = {
      ...all,
      features: all.features.filter(f => this._info(f)),
    }

    const layer = new GeoJSON(africaGeo, {
      style:         f => this._style(f),
      onEachFeature: (f, l) => this._bind(f, l),
    }).addTo(this._map)

    // Fill non-AMP countries with the diagonal hatch <pattern> defined in the
    // view. Re-applied on zoom in case Leaflet re-paints the paths.
    this._geoLayer = layer
    this._paintHatch()
    // moveend covers the initial fitBounds settle (zoomend alone can miss it);
    // zoomend keeps the hatch after Leaflet re-paints paths on zoom.
    this._map.on("zoomend moveend", () => this._paintHatch())

    // Default view: fit to data countries, fall back to all Africa. Stored so
    // the reset control can fly back to it.
    const dataFeatures = africaGeo.features.filter(f => this._countryRecord(f))
    const boundsSource = dataFeatures.length > 0 ? dataFeatures : africaGeo.features
    this._defaultBounds = new GeoJSON({ type: "FeatureCollection", features: boundsSource }).getBounds()
    // Fit the AMP countries with a modest margin — shows the whole region
    // (roughly the continent) without clipping edge countries.
    this._map.fitBounds(this._defaultBounds, { padding: [12, 12] })
    this._defaultCenter = this._map.getCenter()
    this._defaultZoom   = this._map.getZoom()

    // Freeze the extent so users can't pan or zoom out past the initial view.
    this._map.setMaxBounds(this._defaultBounds.pad(0.3))
    this._map.setMinZoom(this._defaultZoom)

    // Paint the non-AMP hatch now the view has settled. On the very first paint
    // the country <path>s can still be missing, so repaint on the next frame too
    // — this makes the stripes show on load without needing a zoom or pan.
    this._paintHatch()
    requestAnimationFrame(() => this._paintHatch())

    this._addResetControl()
    this._renderMarkers()
  }

  _renderMarkers() {
    const sites = this.sitesValue || []
    sites.forEach(site => {
      if (site.lat == null || site.lng == null) return
      const color = STATUS_COLORS[site.status] || "#6b7280"
      new CircleMarker([site.lat, site.lng], {
        radius:      7,
        fillColor:   color,
        color:       "#fff",
        weight:      2,
        opacity:     1,
        fillOpacity: 0.9,
      }).addTo(this._map)
    })
  }

  // Adds a "Reset to Africa" button below the zoom control.
  _addResetControl() {
    const labels = this.labelsValue || {}
    const ctl = new Control({ position: "topleft" })
    ctl.onAdd = () => {
      const btn = DomUtil.create("button", "amp-map-reset leaflet-bar")
      btn.type = "button"
      btn.title = labels.reset || "Reset to Africa"
      btn.setAttribute("aria-label", btn.title)
      btn.innerHTML = "&#x2302;" // house/home glyph; styled via CSS
      btn.addEventListener("click", e => {
        e.preventDefault()
        e.stopPropagation()
        this._resetView()
      })
      return btn
    }
    ctl.addTo(this._map)
  }

  _resetView() {
    this._clearSelection()
    this._showLegend()
    if (this._defaultBounds) {
      this._map.flyTo(this._defaultCenter, this._defaultZoom, { duration: 0.6 })
    }
  }

  // Fills every non-AMP country path with the diagonal hatch <pattern> defined
  // in the view (referenced as fill="url(#amp-nonamp-hatch)").
  _paintHatch() {
    this._geoLayer?.eachLayer(l => {
      if (!this._countryRecord(l.feature) && l._path) {
        l._path.setAttribute("fill", `url(#${HATCH_ID})`)
      }
    })
  }

  // Resolves the country descriptor for a feature by ISO numeric id.
  _info(feature) {
    return AFRICA[+feature.id] || null
  }

  // Display name: prefer the admin-curated record name, fall back to the
  // dataset's own name.
  _displayName(feature, record) {
    const info = this._info(feature)
    return record?.name || info?.name || ""
  }

  // Returns the Amp::Country record for this feature, or null if none exists.
  _countryRecord(feature) {
    const info = this._info(feature)
    if (!info) return null
    return this.ampCountriesValue?.[info.iso2] || null
  }

  // Live site count from the legacy countries hash (fallback when the admin
  // amp_sites_count is blank).
  _liveSiteCount(feature) {
    const info = this._info(feature)
    if (!info) return 0
    return this.countriesValue?.[info.iso2] || this.countriesValue?.[info.name] || 0
  }

  _style(feature) {
    const record   = this._countryRecord(feature)
    const selected = record && record.iso2 === this._selectedIso
    return {
      fillColor:   record ? (COUNTRY_STATUS_COLORS[record.status] || COUNTRY_STATUS_COLORS.in_development) : NON_AMP_FILL,
      weight:      selected ? 3 : (record ? 1.5 : 1),
      // White separators between coloured AMP fills; a defined grey outline for
      // non-AMP countries so every country boundary reads (matches the legend).
      color:       selected ? "#0f3d56" : (record ? "#ffffff" : "#9ca3af"),
      // AMP fills are softened so the OSM basemap shows through; non-AMP stays
      // more opaque so the "Non-AMP" hatch stays legible over the basemap.
      fillOpacity: selected ? 0.85 : (record ? 0.6 : 0.92),
    }
  }

  _bind(feature, layer) {
    const info = this._info(feature)
    if (!info) return

    const record = this._countryRecord(feature)
    if (record) this._layersByIso[record.iso2] = layer

    // Rich country card as an INTERACTIVE tooltip. Leaflet tooltips open on
    // hover natively (unlike popups, whose openPopup() didn't fire reliably on
    // these multi-polygon layers); interactive:true keeps the card open while
    // the cursor is over it, so the "View Full Project Details" link is clickable.
    // Rich country card shown on HOVER as a non-interactive tooltip (reliable,
    // no flicker). pointer-events:none (CSS) keeps it transparent to the mouse.
    // Click the country to open the full, interactive detail panel (with link).
    layer.bindTooltip(this._popupHtml(feature, info), {
      interactive: false,
      direction:   "top",
      offset:      [0, -8],
      opacity:     1,
      className:   "amp-country-card",
    })

    // Hover restyle only for AMP countries — re-applying setStyle on a non-AMP
    // layer would overwrite its hatch fill with the plain white fillColor.
    layer.on("mouseover", () => { if (record && record.iso2 !== this._selectedIso) layer.setStyle({ fillOpacity: 0.85, weight: 2 }) })
    layer.on("mouseout",  () => { if (record && record.iso2 !== this._selectedIso) layer.setStyle(this._style(feature)) })
    layer.on("click", () => this._selectCountry(feature, info))
  }

  _selectCountry(feature, info) {
    const record = this._countryRecord(feature)
    this._clearSelection()
    if (!record) {
      this._showLegend()
      return
    }
    this._selectedIso = record.iso2
    this._layersByIso[record.iso2]?.setStyle(this._style(feature))
    this._showDetail(record, feature)
  }

  _clearSelection() {
    const prev = this._selectedIso
    this._selectedIso = null
    if (prev && this._layersByIso[prev]) {
      const l = this._layersByIso[prev]
      l.setStyle(this._style(l.feature))
    }
  }

  // --- Contextual info panel (sidebar) --------------------------------------

  _showLegend() {
    if (this.hasLegendTarget) this.legendTarget.hidden = false
    if (this.hasDetailTarget) { this.detailTarget.hidden = true; this.detailTarget.innerHTML = "" }
  }

  _showDetail(record, feature) {
    if (!this.hasDetailTarget) return
    const L = this.labelsValue || {}
    const rows = []

    const sites = record.amp_sites_count != null ? record.amp_sites_count : this._liveSiteCount(feature)
    const push = (label, value) => { if (value != null && value !== "") rows.push([label, value]) }

    push(L.sites || "Sites", sites != null ? `${fmtNum(sites)} ${L.amp_sites || "AMP sites"}` : null)
    push(L.capacity || "Capacity",          record.capacity_kwp != null ? `${fmtNum(record.capacity_kwp)} kWp` : null)
    if (record.potential_sites != null && Number(record.potential_sites) > 0) {
      push(L.potential_sites || "Planned AMP sites", fmtNum(record.potential_sites))
    }
    push(L.beneficiaries || "Beneficiaries", fmtNum(record.beneficiaries))
    push(L.carbon || "Carbon",               record.carbon_kg != null ? `${fmtNum(record.carbon_kg)} kg` : null)
    push(L.pue || "PUE",                     fmtNum(record.pue))
    push(L.tariff || "Tariff",               record.tariff != null ? `${fmtNum(record.tariff)} ${esc(record.currency || "")}`.trim() : null)

    const rowsHtml = rows.map(([k, v]) =>
      `<div class="amp-panel-row"><span class="amp-panel-key">${esc(k)}:</span> ` +
      `<span class="amp-panel-val">${esc(v)}</span></div>`
    ).join("")

    const swatch = `<span class="amp-panel-swatch" style="background:${esc(record.color)}"></span>`
    const link = record.platform_url
      ? `<a class="amp-panel-link" href="${esc(record.platform_url)}" target="_blank" rel="noopener">` +
        `${esc(L.view_national_platform || "View National Platform")}</a>`
      : ""
    const description = record.description
      ? `<div class="amp-panel-desc">${esc(record.description)}</div>`
      : ""

    this.detailTarget.innerHTML =
      `<div class="amp-panel-head">${swatch}<span class="amp-panel-country">${esc(this._displayName(feature, record))}</span></div>` +
      `<div class="amp-panel-status">${esc(record.status_label || record.status)}</div>` +
      rowsHtml + description + link

    this.detailTarget.hidden = false
    if (this.hasLegendTarget) this.legendTarget.hidden = true
  }

  // Builds the country-overview click-popup: AMP brand, title, and a compact
  // grid of admin-curated key indicators (Potential / Active sites, Beneficiaries).
  _popupHtml(feature, info) {
    const L      = this.labelsValue || {}
    const record = this._countryRecord(feature)
    const name   = esc(this._displayName(feature, record))

    if (!record) {
      return `<div class="amp-popup"><div class="amp-popup-name">${name}</div>` +
             `<div class="amp-popup-muted">${esc(L.no_record || "No deployment record")}</div></div>`
    }

    const activeSites = record.amp_sites_count != null ? record.amp_sites_count : this._liveSiteCount(feature)
    const metrics = [
      { icon: OVERVIEW_ICONS.potential, color: "#0f9d8a", value: record.potential_sites, label: L.potential_sites || "Potential Sites" },
      { icon: OVERVIEW_ICONS.active,    color: "#16a34a", value: activeSites,             label: L.active_sites   || "Active Sites" },
      { icon: OVERVIEW_ICONS.people,    color: "#1B9DD9", value: record.beneficiaries,    label: L.beneficiaries  || "Beneficiaries" },
    ]
    const cards = metrics.map(m =>
      `<div class="amp-ov-metric">` +
        `<span class="amp-ov-icon" style="color:${m.color}">${m.icon}</span>` +
        `<span class="amp-ov-num">${m.value != null ? fmtNum(m.value) : "—"}</span>` +
        `<span class="amp-ov-lbl">${esc(m.label)}</span>` +
      `</div>`
    ).join("")

    const logo = L.amp_logo ? `<img class="amp-ov-logo" src="${esc(L.amp_logo)}" alt="AMP">` : ""
    const link = record.platform_url
      ? `<a class="amp-ov-link" href="${esc(record.platform_url)}" target="_blank" rel="noopener">` +
        `${esc(L.view_full_details || L.view_national_platform || "View Full Project Details")}</a>`
      : ""

    return `<div class="amp-popup amp-overview">` +
           `<div class="amp-ov-brand">${logo}</div>` +
           `<div class="amp-ov-title">${name} — ${esc(L.country_overview || "Country Overview")}</div>` +
           `<div class="amp-ov-rule"></div>` +
           `<div class="amp-ov-section">${esc(L.key_indicators || "Key Indicators")}</div>` +
           `<div class="amp-ov-grid">${cards}</div>` +
           link +
           `</div>`
  }
}
